import { inject, Injectable, signal } from '@angular/core';

import { ApiService, ProductCapabilitiesService } from '@core';
import { mapProductDetailsCartToInternal, ProductDetailsCart, ProductDetailsCartDto } from '@models';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CartDataService {
  private readonly apiService = inject(ApiService);
  private readonly capabilitiesService = inject(ProductCapabilitiesService);

  readonly productDetailsCart = signal<ProductDetailsCart | null>(null);

  fetchProductCart(): Observable<ProductDetailsCart> {
    return (this.apiService.get('cart') as Observable<ProductDetailsCartDto>).pipe(
      map(mapProductDetailsCartToInternal),
      tap((cart) => {
        this.productDetailsCart.set(cart);
      }),
    );
  }

  updateProduct(vars: {
    productId: number;
    quantity: number;
    variantSelection?: any;
    lineNumber?: number;
  }): Observable<unknown> {
    return this.apiService.patch('cart/update', vars);
  }

  removeProduct(productId: number, lineNumber?: number): Observable<unknown> {
    if (lineNumber !== undefined) {
      return this.apiService.delete(`cart/${productId}/remove/${lineNumber}`);
    }
    return this.apiService.delete(`cart/${productId}/remove`);
  }

  optimisticProductUpdate(productId: number, quantity: number, lineNumber?: number): void {
    this.productDetailsCart.update((cart) => {
      if (!cart) return null;
      // RCP-452 — a SKU can span several lines (pre-RCP-379 Apple carts): update only the clicked one.
      const products =
        lineNumber === undefined
          ? updateOrAddProduct(cart.products, { productId, quantity })
          : cart.products.map((p) =>
              p.productId === productId && p.lineNumber === lineNumber ? { ...p, quantity } : p,
            );
      return {
        ...cart,
        products: products as any,
      };
    });
  }

  optimisticProductRemove(productId: number, lineNumber?: number): void {
    this.productDetailsCart.update((cart) => {
      if (!cart) return null;
      return {
        ...cart,
        products: cart.products.filter((p) => {
          // If lineNumber is provided, remove only the specific line
          if (lineNumber !== undefined) {
            return !(p.productId === productId && p.lineNumber === lineNumber);
          }
          // Otherwise, remove all items with this productId (legacy behavior)
          return p.productId !== productId;
        }),
      };
    });
  }
}

export const updateOrAddProduct = (
  products: { productId: number; quantity: number }[],
  newProduct: { productId: number; quantity: number },
) => {
  const index = products.findIndex((product) => product.productId === newProduct.productId);

  if (index !== -1) {
    products[index] = { ...products[index], quantity: newProduct.quantity };
  } else {
    products.push(newProduct);
  }

  return products;
};
